import { GameConstants } from "../../GameConstants";
import { GameVars } from "../../GameVars";
import { Block } from "./Block";
import { Niche } from "./niche/Niche";
import { GUI } from "./GUI";
import { AudioManager } from "../../AudioManager";
import * as Templok from "@ravalmatic/templok-engine";

export class BlocksDispenser extends Phaser.GameObjects.Container {

    public static readonly PY = 508;
    public static readonly DX = 155;
    public static readonly NICHE_R = 110;

    private static handCursorShown: boolean;
    private static color: number;
    
    public nicheRect: {x: number, y: number, width: number, height: number}; 
    public blocks: Block [];
    public niche: Niche;
    public handCursor: Phaser.GameObjects.Image;

    private py: number;
    
    constructor(scene: Phaser.Scene) {
        
        super(scene);

        BlocksDispenser.handCursorShown = false;
        BlocksDispenser.color = 1;

        this.y = BlocksDispenser.PY; 

        this.blocks = [];
        
        this.py = -40;

        this.niche = new Niche(this.scene, 295, this.py, this);
        this.add(this.niche);

        this.nicheRect = {x: 1.2 * BlocksDispenser.DX, y: -20 + this.py, width: .6 * BlocksDispenser.DX, height: 120};

        if (GameConstants.DEBUG_MODE) {
            this.drawDebugGraphics();
        }
    }
    
    public update(): void {

        for (let i = 0; i < this.blocks.length; i ++) {
            this.blocks[i].update();
        }

        if (this.niche.block) {
            this.niche.block.update();
        }

        this.niche.update();
    }

    public onGameStarted(): void {

        this.niche.onGameStarted();

        // TODO: mirar que bloques hay en el dispensador
        const blocksOnDispenser = GameVars.templokEngine.blocksOnDispenser;

        for (let i = 0; i < blocksOnDispenser.length; i ++) {
            this.spawnBlock(blocksOnDispenser[i]);
        }

        this.checkBlocks();
    }

    public spawnBlock(blockData: Templok.BlockData): void {

        const px = (-1.75 + this.blocks.length) * BlocksDispenser.DX;
        const py = this.py;
        const block = new Block(this.scene, this, blockData, BlocksDispenser.color, px, py);
        this.add(block);

        block.appear(this.blocks.length);

        this.blocks.push(block);

        BlocksDispenser.color = BlocksDispenser.color < GameConstants.BLOCKS_NUM_COLOURS ? BlocksDispenser.color + 1 : 1;
    }

    public returnToOrigin(block: Block, droppedOutsideBoard?: boolean): void {

        if (block.isOnNiche) {
            this.moveBlockToNiche(block);
        } else {
            block.returnToOrigin();
        }

        if (!droppedOutsideBoard) {
            AudioManager.playSound("piece_released_wrong");
        }
    }

    public moveBlockToNiche(block: Block): void {

        this.niche.addBlock(block);

        const i = this.blocks.indexOf(block);
        
        if (i !== -1) {
            this.blocks.splice(i, 1);
        }
    }

    public onBlockAddedToGrid(block: Block): void {

        if (block.isOnNiche) {
            this.niche.removeBlock();
        } else {

            let i = -1;

            for (let j = 0; j < this.blocks.length; j ++) {
                if (block.blockData.id === this.blocks[j].blockData.id) {
                    i = j;
                    break;
                }
            }

            if (i !== -1) {
                this.blocks.splice(i, 1);
            } 
        }
    }

    public checkBlocks(): void {

        const engine = GameVars.templokEngine;

        for (let i = 0; i < engine.blocksOnDispenser.length; i++) {

            const blockData = engine.blocksOnDispenser[i];

            if (engine.doesBlockFitInBoard(blockData)) {
                this.blocks[i].enable();
            } else {
                this.blocks[i].disable();
            }
        }

        if (this.niche.block) {

            if (engine.doesBlockFitInBoard(engine.blockOnNicheData)) {
                this.niche.block.enable();
            } else {
                this.niche.block.disable();
            }
        }

        if (engine.blockOnNicheData) {
            
            if (this.isRerollTheOnlyValidMove()) {
                GUI.currentInstance.showHandCursorOnRerollButton();
            }
            
        } else {

            if (this.blocks.length === 1 && !this.blocks[0].enabled && !BlocksDispenser.handCursorShown) {
                this.showHandCursor();
            }
        }
    }

    public removeHandCursor(): void {

        if (this.handCursor)  {
            this.handCursor.destroy();
            this.handCursor = null;
        }
    }

    public getBlockById(blockId: number): Block {

        let block: Block = null;

        for (let i = 0; i < this.blocks.length; i ++) {

            if (this.blocks[i].blockData.id === blockId) {
                block = this.blocks[i];
                break;
            }
        }

        if (block === null && this.niche.block !== null) {
            block = this.niche.block;
        }

        return block;
    }

    private showHandCursor(): void {

        BlocksDispenser.handCursorShown = true;

        const x = this.blocks[0].x;
        const y = this.blocks[0].y - this.blocks[0].py;

        this.handCursor = new Phaser.GameObjects.Image(this.scene, x, y, "texture_atlas_1", "hand_cursor");
        this.handCursor.setScale(.65);
        this.handCursor.setOrigin(.3);
        this.handCursor.angle = -35;
        this.add(this.handCursor);

        this.scene.tweens.add({
            targets: this.handCursor,
            x: this.niche.x,
            ease: Phaser.Math.Easing.Cubic.Out,
            duration: 1500,
            repeat: -1
        });
    }

    private drawDebugGraphics(): void {

        const debugGraphics = new Phaser.GameObjects.Graphics(this.scene);
        debugGraphics.lineStyle(1, 0xFFFF00, .45);

        debugGraphics.moveTo(-GameConstants.GAME_WIDTH / 2, this.py);
        debugGraphics.lineTo(GameConstants.GAME_WIDTH / 2, this.py);
        
        debugGraphics.moveTo(-1.75 * BlocksDispenser.DX, -70 + this.py);
        debugGraphics.lineTo(-1.75 * BlocksDispenser.DX, 150 + this.py);

        debugGraphics.moveTo(-.75 * BlocksDispenser.DX, -70 + this.py);
        debugGraphics.lineTo(-.75 * BlocksDispenser.DX, 150 + this.py);
        
        debugGraphics.moveTo(.25 * BlocksDispenser.DX, -70 + this.py);
        debugGraphics.lineTo(.25 * BlocksDispenser.DX, 150 + this.py);

        debugGraphics.moveTo(this.niche.x, -130 + this.py);
        debugGraphics.lineTo(this.niche.x, 150 + this.py);

        debugGraphics.stroke();

        // el nicho de los bloques reservados
        debugGraphics.strokeCircle(this.niche.x, this.niche.y, BlocksDispenser.NICHE_R);
 
        this.add(debugGraphics);        
    }

    private isRerollTheOnlyValidMove(): boolean {

        const engine = GameVars.templokEngine;

        // no hay ninguno en el nicho o bien se van a instanciar 3 bloques
        if (engine.blocksOnDispenser.length === 0) {

            return false;

        } else {

            // ¿SE PUEDE COLOCAR ALGUNA PIEZA TAL CUAL?
            for (let i = 0; i < engine.blocksOnDispenser.length; i ++) {

                const blockData = engine.blocksOnDispenser[i];
        
                if (engine.doesBlockFitInBoard(blockData)) {
                    return false;
                }
            }

            if (engine.doesBlockFitInBoard(engine.blockOnNicheData)) {
                return false;
            }

            // ¿SE PUEDE COLOCAR LA PIEZA DEL NICHO ROTADA?
            if (engine.spins > 0 && engine.doesRotatedBlockFitInBoard(engine.blockOnNicheData)) {
                return false;
            }

            if (engine.reRolls > 0) {
                return true;
            }
        }
    
        return false;
    }
}
